import React, { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Clock3, Car, Truck, Repeat, Eye } from "lucide-react";
import type { ShiftRow, DragData } from "./types";
import { getShiftColour, getStatusBadgeVariant, driverDisplayName } from "./utils";
import { loadVehicleTypes, getIconStyle, type VehicleTypeRow } from "@/lib/vehicleTypes";

interface ShiftCardProps {
  shift: ShiftRow;
  onEdit: (shift: ShiftRow) => void;
  compact?: boolean;
}

const useVehicleTypeIcon = (vehicleType?: string | null) => {
  const [types, setTypes] = useState<VehicleTypeRow[]>([]);
  useEffect(() => {
    loadVehicleTypes().then(setTypes);
  }, []);
  const style = getIconStyle(vehicleType, types);
  return style === "truck" ? <Truck className="h-3 w-3" /> : <Car className="h-3 w-3" />;
};

const getShiftVisualStyle = (
  shift: ShiftRow,
  compact = false
): React.CSSProperties => {
  const colour = getShiftColour(shift);
  const isDraft = shift.status === "draft";
  const isCancelled = shift.status === "cancelled";
  const isAvailable = shift.status === "available";
  const isSpecial = !shift.counts_as_worked_hours;

  if (isCancelled) {
    return {
      backgroundColor: `${colour}10`,
      borderLeft: `3px solid ${colour}`,
      opacity: 0.4,
    };
  }

  if (isDraft) {
    return {
      backgroundColor: compact ? `${colour}10` : `${colour}12`,
      border: `2px dashed ${colour}`,
      borderLeft: `3px solid ${colour}`,
    };
  }

  if (isAvailable) {
    return {
      backgroundColor: `${colour}20`,
      border: `2px dashed ${colour}`,
      borderLeft: `3px solid ${colour}`,
    };
  }

  if (isSpecial) {
    return {
      backgroundColor: `${colour}20`,
      border: `1px solid ${colour}66`,
      borderLeft: `3px solid ${colour}`,
    };
  }

  return {
    backgroundColor: `${colour}CC`,
    border: `1px solid ${colour}`,
    borderLeft: `3px solid ${colour}`,
  };
};

const getPrimaryTextColour = (shift: ShiftRow): string => {
  if (
    shift.status === "published" ||
    shift.status === "assigned" ||
    shift.status === "completed"
  ) {
    return "#FFFFFF";
  }
  return getShiftColour(shift);
};

const getSecondaryTextColour = (shift: ShiftRow): string => {
  if (
    shift.status === "published" ||
    shift.status === "assigned" ||
    shift.status === "completed"
  ) {
    return "rgba(255,255,255,0.75)";
  }
  return "#D1D5DB";
};

const ShiftCard: React.FC<ShiftCardProps> = ({
  shift,
  onEdit,
  compact = false,
}) => {
  const colour = getShiftColour(shift);
  const isCancelled = shift.status === "cancelled";
  const isDraft = shift.status === "draft";
  const vehicleTypeIcon = useVehicleTypeIcon(shift.vehicle_type);

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    const dragData: DragData = {
      shiftId: shift.id,
      sourceDate: shift.shift_date,
    };

    e.dataTransfer.setData("application/json", JSON.stringify(dragData));
    e.dataTransfer.effectAllowed = "move";
    e.currentTarget.classList.add("opacity-40");
  };

  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    e.currentTarget.classList.remove("opacity-40");
  };

  const visualStyle = getShiftVisualStyle(shift, compact);
  const primaryText = getPrimaryTextColour(shift);
  const secondaryText = getSecondaryTextColour(shift);

  if (compact) {
    return (
      <div
        draggable={!isCancelled}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        className={`rounded px-1.5 py-1 cursor-grab active:cursor-grabbing transition-all hover:ring-1 hover:ring-white/30 text-[10px] ${
          isCancelled ? "opacity-40" : ""
        }`}
        style={visualStyle}
        onClick={(e) => {
          e.stopPropagation();
          onEdit(shift);
        }}
        title={`${driverDisplayName(shift)} — ${shift.shift_type_label} ${shift.start_hhmm}–${shift.end_hhmm}${
          shift.vehicle_number ? ` (${shift.vehicle_number})` : ""
        }`}
      >
        <div className="flex items-center justify-between gap-1">
          <span className="font-semibold truncate" style={{ color: primaryText }}>
            {driverDisplayName(shift)}
          </span>
          <span className="whitespace-nowrap" style={{ color: secondaryText }}>
            {shift.start_hhmm}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      draggable={!isCancelled}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      className={`rounded p-1.5 cursor-grab active:cursor-grabbing transition-all hover:ring-1 hover:ring-white/30 ${
        isCancelled ? "opacity-40" : ""
      }`}
      style={visualStyle}
      onClick={(e) => {
        e.stopPropagation();
        onEdit(shift);
      }}
    >
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold truncate" style={{ color: primaryText }}>
          {driverDisplayName(shift)}
        </span>
        <div className="flex items-center gap-1">
          {shift.repeat_group_id && (
            <Repeat className="h-3 w-3 text-purple-400" title="Repeating shift" />
          )}
          {isDraft && (
            <Eye className="h-3 w-3 text-amber-400" title="Draft / unpublished" />
          )}
          <Badge
            variant={getStatusBadgeVariant(shift.status)}
            className="text-[9px] px-1 py-0 h-4"
          >
            {shift.status}
          </Badge>
        </div>
      </div>

      <div className="flex items-center gap-1 mt-0.5">
        <Clock3 className="h-3 w-3" style={{ color: secondaryText }} />
        <span className="text-[10px]" style={{ color: secondaryText }}>
          {shift.start_hhmm}–{shift.end_hhmm}
        </span>
      </div>

      <div className="flex items-center gap-1 mt-0.5">
        {shift.vehicle_number ? (
          <>
            <span style={{ color: secondaryText }}>
              {vehicleTypeIcon}
            </span>
            <span className="text-[10px]" style={{ color: secondaryText }}>
              {shift.vehicle_number}
            </span>
          </>
        ) : (
          <span className="text-[10px] text-yellow-400">No vehicle</span>
        )}
        <Badge
          variant="outline"
          className="ml-auto text-[9px] px-1 py-0 h-4"
          style={{
            borderColor:
              shift.status === "published" ||
              shift.status === "assigned" ||
              shift.status === "completed"
                ? "rgba(255,255,255,0.35)"
                : colour,
            color:
              shift.status === "published" ||
              shift.status === "assigned" ||
              shift.status === "completed"
                ? "#FFFFFF"
                : colour,
          }}
        >
          {shift.shift_type_code}
        </Badge>
      </div>
    </div>
  );
};

export default ShiftCard;